import React, { useEffect, useRef, useState } from "react";
import { Button, FormLabel, TextField, Tooltip } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateImportantAnnouncementMutation,
  useLazyGetImportantAnnouncementDetailsByIdQuery,
  useUpdateImportantAnnouncementMutation,
} from "../../../app/services/importantAnnouncementService";
import { toast } from "react-toastify";
import Spinner from "../../../Component/spinner/spinner";
import { Editor } from "primereact/editor";
import { useSelector } from "react-redux";
import { selectCurrentScope, selectUserId } from "../../../app/auth/authSlice";
import { selectCurrentSig } from "../../../app/features/sigSlice";
import { selectCurrentChapterAdmin } from "../../../app/features/chapterAdminSlice";
import InfoIcon from '@mui/icons-material/Info';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file

const AddEditImportAnnounment = () => {
  const {
    handleSubmit,
    setValue,
    register,
    watch,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      text: "",
      expiryDate: "",
      currentCustomName: "",
      files: [], // [{ base64, name, customName, fileObj? }]
      filesToDelete: []
    },
  });

  // Ref for the hidden file input (Chapter logo style)
  const fileInputRef = useRef(null);

  const [text, setText] = useState("");
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const [createAnnouncement] = useCreateImportantAnnouncementMutation();
  const [getAnnouncementById] = useLazyGetImportantAnnouncementDetailsByIdQuery();
  const [updateAnnouncement] = useUpdateImportantAnnouncementMutation();

  const userId = useSelector(selectUserId);
  const memberScope = useSelector(selectCurrentScope);
  const sigDetails = useSelector(selectCurrentSig);
  const chapterDetails = useSelector(selectCurrentChapterAdmin);
  const [approveStatus, setApproveStatus] = useState('');

  // Watch RHF-managed fields
  const expiryDate = watch("expiryDate");
  const currentCustomName = watch("currentCustomName");
  const files = watch("files");

  const sigId = sigDetails?._id;
  const chapterId = chapterDetails?._id;

  // Fetch existing data if `id` is provided
  useEffect(() => {
    if (id) {
      setLoading(true);
      getAnnouncementById(id).then((res) => {
        if (res?.data?.status) {
          setText(res.data.data.text);
          setValue("text", res.data.data.text);

          if (res.data.data.expiryDate) {
            setValue("expiryDate", res.data.data.expiryDate.split('T')[0]);
          }

          if (Array.isArray(res.data.data.files)) {
            const payloadFilenames = res.data.data.fileName || [];
            setValue(
              "files",
              res.data.data.files.map((fileObj, idx) => ({
                ...fileObj,
                customName: payloadFilenames[idx] || fileObj.fileName || "Attached File",
              }))
            );
          } else if (res.data.data.file) {
            setValue("files", [
              {
                base64: res.data.data.file,
                name: res.data.data.fileName || "Attached File",
                customName:
                  (Array.isArray(res.data.data.fileName)
                    ? res.data.data.fileName[0]
                    : res.data.data.fileName) ||
                  res.data.data.fileName ||
                  "Attached File",
              },
            ]);
          }

          setLoading(false);
          setApproveStatus(res.data.data.isApproved);
        }
      });
    }
  }, [id, getAnnouncementById, setValue]);

  const handleContentChange = (value) => {
    setError('');
    if (value.split(' ').length <= 150) {
      setText(value);
      setError('');
    } else {
      setError(`Maximum words limit of 150 reached.`);
    }
  };

  const handleValidation = () => {
    const cleanText = text?.replace(/<[^>]*>/g, '').trim();
    if (!cleanText) {
      setError('Please enter the text!');
      return false;
    }
    setError('');
    return true;
  };

  const navigateBasedOnScope = () => {
    switch (memberScope) {
      case "PORTAL_SUPER_ADMIN":
        navigate("/admin/importannoucenment");
        break;
      case "PORTAL_ADMIN":
        navigate("/assistantAdmin/importannoucenment");
        break;
      case "PORTAL_CHAPTER_ADMIN":
        navigate("/chapterAdmin/importannoucenment");
        break;
      case "PORTAL_SIG_ADMIN":
        navigate("/sigAdmin/importannoucenment");
        break;
      default:
        break;
    }
  };

  const onSaveAnnounment = () => {
    const filesArray = Array.isArray(files) ? files : [];
    if (filesArray.length === 0) {
      toast.error("At least one file upload is mandatory!");
      return;
    }
    const missingName = filesArray.some(f => !f.customName || !f.customName.trim());
    if (missingName) {
      toast.error("All uploaded files must have a file name!");
      return;
    }
    if (!handleValidation()) {
      return;
    }

    const formData = new FormData();
    formData.append("text", text);
    formData.append("isApproved", approveStatus || "");
    formData.append("expiryDate", expiryDate);

    const names = [];
    filesArray.forEach((f) => {
      if (f.fileObj) {
        formData.append("files", f.fileObj);
        names.push(f.customName);
      } else {
        formData.append("files", f.fileUrl.split("/web/")[1]);
        names.push(f.fileName);
      }
    });
    formData.append("fileNames", JSON.stringify(names));
    if (id) {
      if (getValues('filesToDelete')) {
        formData.append("filesToDelete", JSON.stringify(getValues('filesToDelete')));
      }
    }

    if (
      (userId && memberScope === "PORTAL_SUPER_ADMIN") ||
      (userId && memberScope === "PORTAL_SIG_ADMIN") ||
      (userId && memberScope === "PORTAL_ADMIN") ||
      (userId && memberScope === "PORTAL_CHAPTER_ADMIN")
    ) {
      formData.append("createdBy[id]", userId);
      formData.append("createdBy[role]", memberScope);
    }

    if (memberScope === "PORTAL_SIG_ADMIN") {
      formData.append("SIGGroupsId", JSON.stringify([sigId]));
    } else if (memberScope === "PORTAL_CHAPTER_ADMIN") {
      formData.append("chaptersId", JSON.stringify([chapterId]));
    }

    const action = id
      ? updateAnnouncement({ id, data: formData })
      : createAnnouncement(formData);

    setLoading(true);
    action
      .then((res) => {
        if (res?.data?.status) {
          toast.success(`Announcement ${id ? "Updated" : "Created"} Successfully!`);
          navigateBasedOnScope();
        } else {
          toast.error("Failed to save the announcement!");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    if (!currentCustomName || !currentCustomName.trim()) {
      toast.error("Please enter a File Name before uploading files!");
      e.target.value = "";
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    const currentFiles = Array.isArray(files) ? files : [];
    if (currentFiles.length + selectedFiles.length > MAX_FILES) {
      toast.error(`You can upload a maximum of ${MAX_FILES} files!`);
      e.target.value = "";
      return;
    }

    const validFiles = [];
    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`"${file.name}" is not a supported file type (DOC, DOCX, PDF, JPEG, PNG, WebP only)!`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds the 5MB size limit!`);
        continue;
      }
      validFiles.push(file);
    }

    if (!validFiles.length) {
      e.target.value = "";
      return;
    }

    let filesProcessed = 0;
    const newFiles = [];

    validFiles.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const suffix = validFiles.length > 1 ? `-${index + 1}` : "";
        newFiles.push({
          base64: event.target.result,
          name: file.name,
          customName: `${currentCustomName}${suffix}`,
          fileObj: file,
        });
        filesProcessed += 1;
        if (filesProcessed === validFiles.length) {
          setValue("files", [...currentFiles, ...newFiles], { shouldValidate: true });
          setValue("currentCustomName", ""); // Clear field for next file upload
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    const currentFiles = Array.isArray(files) ? files : [];
    setValue(
      "files",
      currentFiles.filter((_, i) => i !== index),
      { shouldValidate: true }
    );
    const filesToDelete = getValues('filesToDelete')
    // console.log(currentFiles[index].fileId, "filesToDelete")
    if (currentFiles[index].fileId) {
      setValue('filesToDelete', [...filesToDelete, currentFiles[index].fileId])
    }
  };

  return (
    <div>
      <div className="pt-5 pb-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">
            {id ? "Edit Announcement" : "Add Announcement"}
          </h2>
        </div>
      </div>
      <div className='p-md-5 p-3 overallPadding'>
        <div className="d-flex justify-content-end">
          <Button
            onClick={() => window.history.back()}
            variant="contained"
            className="menus-color"
            style={{ marginRight: '25px', marginBottom: '25px' }}
          >
            Back
          </Button>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <form onSubmit={handleSubmit(onSaveAnnounment)} noValidate>
            <div className="row mb-4">
              {/* Expiry Date - controlled via RHF Controller */}
              <div className="col-md-4" style={{ textAlign: 'start' }}>
                <Controller
                  name="expiryDate"
                  control={control}
                  rules={{
                    required: "Expiry Date is required",
                    validate: (value) => {
                      if (!value) return "Expiry Date is required";
                      const today = new Date().toISOString().split('T')[0];
                      return value >= today || "Expiry date must be greater than or equal to current date";
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label={<>Expiry Date <span style={{ color: "red" }}> *</span></>}
                      error={!!errors.expiryDate}
                      helperText={
                        errors.expiryDate?.message ||
                        "Expiry date must be greater than or equal to current date"
                      }
                    />
                  )}
                />
              </div>

              <div className="col-md-8" style={{ textAlign: 'start' }}>
                <div className="row">
                  <div className='col-md-6'>
                    {/* File Name - controlled via RHF Controller */}
                    <Controller
                      name="currentCustomName"
                      control={control}
                      // rules={{
                      //   validate: (value) =>
                      //     files.length > 0 || (value && value.trim())
                      //       ? true
                      //       : "File Name is required",
                      // }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          label={<>File Name <span style={{ color: "red" }}> *</span></>}
                          {...register("currentCustomName", {
                            validate: (value) =>
                              (Array.isArray(files) && files.length > 0) || (value && value.trim())
                                ? true
                                : "File Name is required",
                          })}
                          placeholder="Enter custom file name (e.g. 1stpsf)"
                          error={!(Array.isArray(files) && files.length > 0) && !!errors.currentCustomName}
                          helperText={!(Array.isArray(files) && files.length > 0) ? errors.currentCustomName?.message : ""}
                        // required={!(Array.isArray(files) && files.length > 0)}
                        />
                      )}
                    />
                  </div>
                  <div className='col-md-6'>
                    {/* File upload — Chapter logo style: hidden input + styled button */}
                    <div
                      className="border border-secondary-subtle rounded d-flex justify-content-between align-items-center px-3 py-2"
                      style={{
                        borderColor: Array.isArray(files) && files.length === 0 && errors.files ? 'red' : undefined,
                        minHeight: '56px',
                      }}
                    >
                      <span className="text-muted" style={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
                        {Array.isArray(files) && files.length > 0
                          ? `${files.length} file(s) selected`
                          : 'No file chosen'}
                      </span>
                      <FormLabel htmlFor="announcementFileInput" style={{ margin: 0 }}>
                        <Button
                          variant="contained"
                          className="menus-color"
                          component="span"
                          size="small"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Choose File <span style={{ color: "red" }}> *</span>
                        </Button>
                      </FormLabel>
                    </div>
                    {/* Hidden native file input */}
                    <input
                      id="announcementFileInput"
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.jpeg,.jpg,.png,.webp"
                      multiple
                      onChange={handleFileChange}
                      className="d-none"
                    />
                    {Array.isArray(files) && files.length === 0 && errors.files && (
                      <p style={{ color: 'red', fontSize: '0.75rem', marginTop: 4 }}>
                        {errors.files.message}
                      </p>
                    )}
                  </div>

                  {Array.isArray(files) && files.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      {files.map((f, index) => (
                        <div
                          key={`${f.name || f.fileName}-${index}`}
                          className="d-flex align-items-center justify-content-between gap-2 border p-2 mb-2 rounded bg-light"
                          style={{ fontSize: "14px" }}
                        >
                          <div>
                            <strong>Name: </strong>{f.customName} <br />
                            <small className="text-muted">({f.customName || f.fileName})</small>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleRemoveFile(index)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <Editor
                  value={text}
                  onTextChange={(e) => {
                    const newText = e.htmlValue;
                    if (newText && newText.replace(/<[^>]+>/g, "").split(' ').length <= 150) {
                      setText(newText);
                      setError("");
                    } else {
                      setError("Maximum 150 words allowed.");
                    }
                  }}
                  style={{
                    height: "320px",
                    border: error ? "2px solid red" : "2px solid lightgrey",
                  }}
                  onChange={handleContentChange}
                  onBlur={handleValidation}
                  placeholder="Please Enter Your Text"
                  headerTemplate={
                    <span className="ql-formats">
                      <select className="ql-header" aria-label="Header" />
                      <button className="ql-bold" aria-label="Bold" />
                      <button className="ql-italic" aria-label="Italic" />
                      <button className="ql-underline" aria-label="Underline" />
                      <button className="ql-strike" aria-label="Strike" />
                      <select className="ql-align" value="" aria-label="Align" />
                      <button className="ql-list" value="ordered" aria-label="Ordered List" />
                      <button className="ql-list" value="bullet" aria-label="Unordered List" />
                      <select className="ql-color" />
                      <select className="ql-background" />
                      <button className="ql-clean" aria-label="Clear Formatting" />
                    </span>
                  }
                />
                {error && <p style={{ color: 'red' }}>{error}</p>}
                <p className="text-danger">{errors?.text?.message}</p>
                <Tooltip title="Maximum words limit of 150" placement="right">
                  <span>
                    <InfoIcon />
                  </span>
                </Tooltip>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 20,
              }}
            >
              <Button
                variant="contained"
                className="menus-color"
                type="submit"
                sx={{ mr: 3, mb: 3 }}
              >
                {id ? "Update" : "Submit"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddEditImportAnnounment;